import { v4 as uuidv4 } from 'uuid';

import { PRODUCTS, SESSION_KEYS, CHANNELS } from '../lib/constants/appConstants';
import ROUTES from '../lib/constants/routes';
import { ModifiedInputs } from '../interfaces/Types';
import { InsuranceHelper } from './InsuranceHelper';
import { DiscountHelper } from './DiscountHelper';
import { ConfigHelper } from './ConfigHelper';

export class RoutingHelper {
  static getHashValueFromLocation = () => window.location.hash.substring(1);

  static resolveNextPageUrlPath = (
    productName: string,
    policyNumber?: string,
    cprNumber?: string,
    productNumber?: string,
    coupon?: string,
    couponDiscountPercentage?: string
  ) => {
    const channel = InsuranceHelper.getChannelInfo();
    const locationHash = RoutingHelper.getHashValueFromLocation();
    const urlData = RoutingHelper.getUrlParams();
    const basePaymentPath = `${window.location.origin}/${ROUTES.paymentAppPath}`;

    const createUrlParams = (sessionKey: string) => {
      const urlParams = new URLSearchParams();
      urlParams.append('sessionKey', sessionKey);
      return urlParams;
    };

    const storeOfferInfo = (sessionKey: string) => {
      RoutingHelper.storeOfferInfoInSession(
        sessionKey,
        policyNumber!,
        productName!,
        cprNumber!,
        productNumber!,
        coupon,
        couponDiscountPercentage,
        channel
      );
    };

    const handleSummaryPage = () => {
      if (policyNumber && cprNumber) {
        const urlParams = new URLSearchParams();
        if (urlData['sessionKey']) {
          urlParams.append('sessionKey', urlData['sessionKey']);
        }
        if (urlData['policyNumber']) {
          const sessionKey = uuidv4();
          storeOfferInfo(sessionKey);
          urlParams.append('sessionKey', sessionKey);
        }
        return `${basePaymentPath}/?${urlParams.toString()}`;
      } else {
        return `/${productName}/${ROUTES.summaryPage}`;
      }
    };

    const handleOfferPage = () => {
      if (policyNumber && cprNumber && productNumber) {
        const sessionKey = urlData['sessionKey'] || uuidv4();
        storeOfferInfo(sessionKey);
        const urlParams = createUrlParams(sessionKey);
        return `/${productName}/${ROUTES.summaryPage}/?${urlParams.toString()}`;
      } else {
        return `/${productName}/${ROUTES.summaryPage}`;
      }
    };

    const handleMoreInputsPage = () => {
      const urlParams = new URLSearchParams();
      if (urlData['policyNumber']) {
        const sessionKey = uuidv4();
        storeOfferInfo(sessionKey);
        urlParams.append('sessionKey', sessionKey);
      } else {
        const sessionKey = urlData['sessionKey'] || uuidv4();
        urlParams.append('sessionKey', sessionKey);
      }
      return `/${productName}/${ROUTES.offerPage}/?${urlParams.toString()}`;
    };

    switch (productName) {
      case PRODUCTS.CAR:
      case PRODUCTS.MOPED:
        if (locationHash === `/${productName}/${ROUTES.calculationPage}`) {
          return `/${productName}/${ROUTES.offerPage}`;
        } else if (locationHash.includes(ROUTES.offerPage)) {
          return handleOfferPage();
        } else if (locationHash.includes(ROUTES.summaryPage)) {
          return handleSummaryPage();
        }
        break;

      case PRODUCTS.HOME:
      case PRODUCTS.VILLA:
      case PRODUCTS.MC:
        if (locationHash === `/${productName}/${ROUTES.calculationPage}`) {
          return `/${productName}/${ROUTES.moreInputsPage}`;
        }
        else if (locationHash.includes(ROUTES.moreInputsPage)) {
          return handleMoreInputsPage();
        } else if (locationHash.includes(ROUTES.offerPage)) {
          const sessionKey = urlData['sessionKey'];
          if (policyNumber && cprNumber && sessionKey && productNumber) {
            storeOfferInfo(sessionKey);
            const urlParams = createUrlParams(sessionKey);
            return `/${productName}/${ROUTES.summaryPage}/?${urlParams.toString()}`;
          } else {
            return `/${productName}/${ROUTES.offerPage}`;
          }
        } else if (locationHash.includes(ROUTES.summaryPage)) {
          return handleSummaryPage();
        }
        break;

      case PRODUCTS.PPI:
      case PRODUCTS.ACCIDENT:
        if (locationHash.includes(ROUTES.offerPage)) {
          if (policyNumber && cprNumber && productNumber) {
            const sessionKey = urlData['sessionKey'] || uuidv4();
            const urlParams = createUrlParams(sessionKey);
            storeOfferInfo(sessionKey);
            return `${basePaymentPath}/?${urlParams.toString()}`;
          } else {
            return `/${productName}/${ROUTES.offerPage}`;
          }
        }
    }

    return '';
  };

  static getUrlParams = () => {
    const params: any = {};
    window.location.href.replace(
      /[?&]+([^=&]+)=([^&]*)/gi,
      (match: string, key: string, value: string) => (params[key] = value)
    );
    return params;
  };

  static storeOfferInfoInSession = (
    sessionKey: string,
    policyNumber: string,
    productName: string,
    customerNumber?: string,
    productNumber?: string,
    coupon?: string,
    couponDiscountPercentage?: string,
    channel?: string,
  ) => {
    const policyInfo = {
      policyNr: policyNumber,
      customerNr: customerNumber || '',
      productCode: productNumber || '',
      coupon: coupon ? coupon : '',
      couponDiscountPercentage: couponDiscountPercentage
        ? couponDiscountPercentage
        : '',
      channel: channel,
      campaignDiscountList: ConfigHelper.getCampaignDetails(productName, channel?.toLowerCase()) || []
    };
    sessionStorage.setItem(sessionKey, JSON.stringify(policyInfo));
  };
  static removePropertyInfoFromSession = () => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );

    if (customerInfo?.propertyData) {
      delete customerInfo.propertyData;
      sessionStorage.setItem(
        SESSION_KEYS.CUSTOMER_INFO,
        JSON.stringify(customerInfo)
      );
    }
  };

  /* lint-disable */
  static resolvePreviousPageUrlPath = (
    productName: string,
    policyNumber?: string,
    cprNumber?: string,
    productNumber?: string,
    coupon?: string,
    couponDiscountPercentage?: string
  ) => {
    let previousPagePath = '';
    const locationHash = RoutingHelper.getHashValueFromLocation();
    const channel = InsuranceHelper.getChannelInfo();
    const urlData = RoutingHelper.getUrlParams();
    const urlParams = new URLSearchParams();
    if (locationHash.includes(`/${productName}/${ROUTES.summaryPage}`)) {
      if (urlData) {
        if (urlData['sessionKey']) {
          urlParams.append('sessionKey', urlData['sessionKey']);
        }
        if (urlData['policyNumber']) {
          const sessionKey = uuidv4();
          RoutingHelper.storeOfferInfoInSession(
            sessionKey,
            urlData['policyNumber'],
            productName,
            cprNumber,
            productNumber,
            coupon,
            couponDiscountPercentage,
            channel
          );
          urlParams.append('sessionKey', sessionKey);
        }
      }
      previousPagePath = `/${productName}/${ROUTES.offerPage}?${urlParams.toString()}`;
    } else if (
      locationHash.includes(`/${productName}/${ROUTES.offerPage}`) &&
      productName !== PRODUCTS.CAR && productName !== PRODUCTS.ACCIDENT && productName !== PRODUCTS.MOPED
    ) {
      if (urlData && urlData['sessionKey']) {
        urlParams.append('sessionKey', urlData['sessionKey']);
        previousPagePath = `/${productName}/${ROUTES.moreInputsPage}?${urlParams.toString()}`;
      }
    }
    return previousPagePath;
  };

  //To Do:- Invoke this function to reset the policy info from session along with new revoke implementation
  static resetSessionData = (productName: string) => {
    const locationHash = RoutingHelper.getHashValueFromLocation();
    if (locationHash.includes(`/${productName}/${ROUTES.offerPage}`)) {
      const urlParams = RoutingHelper.getUrlParams();
      if (urlParams && urlParams['sessionKey']) {
        sessionStorage.removeItem(urlParams['sessionKey']);
      }
    }
  };

  static getProductFromUrl = () => window.location.hash.split('/')[1] || '';

  static resolveFirstPageUrlPath = (productName: string) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    const channel =
      customerInfo?.channel &&
        customerInfo?.channel?.toLowerCase() !== 'gjensidige'
        ? customerInfo.channel
        : 'gjensidige';
    const basePath = `${window.location.origin}`;
    switch (productName) {
      case PRODUCTS.CAR:
        return `${basePath}/${ROUTES.carCalculationPath}`;
      case PRODUCTS.HOME:
        if (channel?.toLowerCase() === CHANNELS.HGF)
          return `${basePath}/${ROUTES.hgfHomeCalculationPath}`;
        else if (channel?.toLowerCase() === CHANNELS.RIKSHEM)
          return `${basePath}/${ROUTES.rikshemHomeCalculationPath}`;
        else if (channel?.toLowerCase() === CHANNELS.KLARABO)
          return `${basePath}/${ROUTES.klaraboHomePath}`;
        else if (channel?.toLowerCase() === CHANNELS.WAHLIN)
          return `${basePath}/${ROUTES.wahlinHomePath}`;
        else return `${basePath}/${ROUTES.homeCalculationPath}`;
      case PRODUCTS.VILLA:
        return `${basePath}/${ROUTES.villaCalculationPath}`;
      case PRODUCTS.MC:
        return `${basePath}/${ROUTES.motorcykelCalculationPath}`;
      case PRODUCTS.MOPED:
        return `${basePath}/${ROUTES.mopedCalculationPath}`;
      case PRODUCTS.ACCIDENT:
        return `${basePath}/${ROUTES.olicksfallCalculationPath}`;
      case PRODUCTS.PPI:
        return `${basePath}/${ROUTES.hgfPPICalculationPath}`;
    }

    return basePath;
  };

  static isRoutingToPayment = (urlPath: string) =>
    urlPath.includes(ROUTES.summaryPage);

  static shouldSaveOfferBeforeRoute = (urlPath: string) =>
    urlPath.includes(ROUTES.offerPage);

  static shouldUpdateOffer = (
    urlPath: string,
    modifiedInputs: ModifiedInputs
  ) => {
    const isInputsModified =
      (modifiedInputs?.selections && modifiedInputs?.selections?.length > 0) ||
      !!modifiedInputs.isAdditionalInputsUpdated;
    const policyDetails = InsuranceHelper.retrievePolicyInfoFromURL();
    const policyNumberFromUrl = InsuranceHelper.getPolicyAndChannelFromUrl()?.policyNumber || '';
    const policyNumber = policyNumberFromUrl || policyDetails.policyNumber;
    return (
      policyNumber &&
      urlPath.includes(ROUTES.moreInputsPage) &&
      isInputsModified
    );
  };

  static getQuestionsStepCode = (product: string, page: string) => {
    let stepCode = '';
    switch (product) {
      case PRODUCTS.CAR:
      case PRODUCTS.MOPED:
        return (stepCode = page === ROUTES.offerPage ? '001' : '002');
      case PRODUCTS.HOME:
      case PRODUCTS.VILLA:
        return (stepCode = page === ROUTES.moreInputsPage ? '001' : '002');
    }
    return stepCode;
  };
  static getLogInUrl = (product: string, isLoggedIn: boolean, redirectPage: string) => {
    const urlData = RoutingHelper.getUrlParams();
    const policyNumber = urlData['policyNumber'];
    const channel = urlData['channel'];
    let redirectUrl = '';
    if (product && urlData && policyNumber) {
      if (!isLoggedIn) {
        redirectUrl =
          `/kund/privat/webshop/%23/${product}/${redirectPage}%3FpolicyNumber=${policyNumber}` ||
          '';
      } else {
        redirectUrl =
          `/kund/privat/webshop/#/${product}/${redirectPage}?policyNumber=${policyNumber}` ||
          '';
      }
    }
    if (channel) {
      redirectUrl += `&channel=${channel}`;
    }
    return redirectUrl;
  };
}
